import { PageBreadcrumbs } from "@molecules";
import { Input } from "@atoms";
import { Layout } from "@layouts";
import { Bar } from "@organisms";
import { Button as SButton, Progress, Input as SInput } from "@shadcn";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { GiftCardOffer, PagePaths, AirdropPagePaths } from "../../../../types";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import { AuthContext } from "@auth";
import { useTranslation } from "react-i18next";

export type AirdropSelectAmountPageTemplateProps = {
  offerSelected: GiftCardOffer | null;
  progress: number;
  onReturnPressed: () => void;
  onContinuePressed: (data: any, giftMessage: string) => void;
  selectedUser: any;
};

export const AirdropSelectAmountPageTemplate = ({
  offerSelected,
  progress,
  onReturnPressed,
  onContinuePressed,
  selectedUser,
}: AirdropSelectAmountPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [typedAmount, setTypedAmount] = useState<number | null>(null);
  const [giftMessage, setGiftMessage] = useState<string>("");
  const navigate = useNavigate();
  const { setSelectedPageIndex } = useContext(AuthContext);
  const goToPerks = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.perks}`);
  };
  const goToAirdrops = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.airdrop}/main`);
  };
  const goToSelectMembers = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectMembers}`);
  };
  const goToSelectGiftCard = () => {
    if (selectedUser) {
      setSelectedPageIndex?.(3);
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectOffers}`, {
        state: { selectedUser },
      });
    } else {
      setSelectedPageIndex?.(3);
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectOffers}`);
    }
  };

  if (!offerSelected) {
    return (
      <Layout
        variant="dashboard"
        header={t("airdrop.common.header")}
        subheader={
          <div className="flex flex-row items-start justify-between max-w-5xl">
            <div className="page-subtitle mt-2">
              {t("airdrop.common.subheader")}
            </div>
          </div>
        }
      >
        <div className="px-1 mr-4 rounded-md max-w-5xl">
          <PageBreadcrumbs
            items={[
              { label: t("airdrop.common.breadcrumbs.benefits"), onClick: goToPerks },
              { label: t("airdrop.common.breadcrumbs.airdrops"), onClick: goToAirdrops },
              { label: t("airdrop.common.breadcrumbs.selectMembers"), onClick: goToSelectMembers },
              { label: t("airdrop.common.breadcrumbs.selectGiftCard") },
            ]}
          />
          <div className="mt-6 text-sm text-neutral-dark">
            {t("airdrop.selectAmount.requireOffer")}
          </div>
        </div>
      </Layout>
    );
  }

  const modifyGiftMessage = (message: string):void => {
    if (message.length < 100) {
      setGiftMessage(message);
    }
  }

  const minAmount =
    offerSelected.activationSettings.forcedMinActivationAmount ??
    offerSelected.activationSettings.minActivationAmount;
  const maxAmount =
    offerSelected.activationSettings.forcedMaxActivationAmount ??
    offerSelected.activationSettings.maxActivationAmount;
  const presetAmounts = offerSelected.presetAmounts.filter(
    (item) => item !== 0,
  );
  const isCustomisable = offerSelected.presetAmounts.includes(0);

  return (
    <Layout
      variant="dashboard"
      header={t("airdrop.common.header")}
      subheader={
        <div className="flex flex-row items-start justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("airdrop.common.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("airdrop.common.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("airdrop.common.breadcrumbs.airdrops"), onClick: goToAirdrops },
            { label: t("airdrop.common.breadcrumbs.selectMembers"), onClick: goToSelectMembers },
            { label: t("airdrop.common.breadcrumbs.selectGiftCard"), onClick: goToSelectGiftCard },
            { label: t("airdrop.common.breadcrumbs.selectAmount") },
          ]}
        />

        <div className="flex flex-col gap-6 border border-neutral-light bg-white rounded-2xl w-full min-w-[400px] p-6">
          <div className="">
            <h2 className="text-xl font-normal">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  3.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("airdrop.selectAmount.title")}
                </span>
              </div>
            </h2>
          </div>
          <div className={`flex flex-row flex-wrap items-start justify-start max-w-5xl rounded-md pb-8`}>
            <div className="rounded-2xl my-2 ml-2 mr-5 min-w-max border border-neutral-medium2 bg-white p-6">
              <img
                src={offerSelected?.images?.[0]?.imageUrl}
                className="rounded-xl h-48 w-80 object-cover"
              />
            </div>
            <div className="flex flex-col justify-start mr-8 my-2 mb-16 pb-2 rounded-md">
              {presetAmounts.length !== 0 ? (
                <div>
                  <h3 className="font-semibold flex justify-start ml-2">
                    {t("airdrop.selectAmount.presetLabel")}
                  </h3>
                  <div className="flex flex-row justify-start mt-2">
                    {presetAmounts.map((item) => {
                      const isSelected = selectedAmount === item;
                      return (
                        <div
                          key={item}
                          className={`w-16 mx-3 bg-white rounded-md flex justify-center items-center transition-colors border-2 ${
                            isSelected
                              ? "bg-primary-medium text-white border-primary-medium"
                              : "bg-neutral-lightmax border-neutral-light hover:border-primary-mediumish"
                          }`}
                        >
                          <SButton
                            variant="ghost"
                            className={`w-full rounded-md ${
                              isSelected ? "hover:bg-transparent text-white hover:text-white" : ""
                            }`}
                            onClick={() => {
                              setSelectedAmount(item);
                              setTypedAmount(0);
                            }}
                          >
                            <span className="font-bold text-lg">{`$${item}`}</span>
                          </SButton>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {isCustomisable ? (
                <div className="ml-2 mt-4 mb-2">
                  <div className="font-semibold flex justify-start">
                    {t("airdrop.selectAmount.customLabel")}
                  </div>
                  <div className="flex items-center justify-start mt-2">
                    <SInput
                      type="number"
                      lang="en-US"
                      min={
                        offerSelected.activationSettings
                          .forcedMinActivationAmount ??
                        offerSelected.activationSettings.minActivationAmount
                      }
                      max={
                        offerSelected.activationSettings
                          .forcedMaxActivationAmount ??
                        offerSelected.activationSettings.maxActivationAmount
                      }
                      placeholder={t("airdrop.selectAmount.customPlaceholder")}
                      step="0.01"
                      value={typedAmount || undefined}
                      onChange={(event) => {
                        setTypedAmount(Number(event?.target.value));
                        setSelectedAmount(null);
                      }}
                      className="h-10 w-40 bg-neutral-lightmax rounded-md border border-neutral-light input-focus-primary"
                    />
                  </div>
                </div>
              ) : null}
              <div className="ml-2 mt-2 font-semibold w-68">
                <div>
                  {t("airdrop.selectAmount.messageLabel")}
                </div>
                <div className="flex items-center justify-start mt-2" style={{ width: 350 }}>
                  <Input
                    name="giftmessage"
                    variant="textArea"
                    onChange={(event) => {
                      modifyGiftMessage(event?.target.value)
                    }}                  
                    placeholder={t("airdrop.selectAmount.messagePlaceholder")}
                    value={giftMessage}
                    errorMessage={""}
                    height={75}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={() => {
            selectedAmount
              ? onContinuePressed(selectedAmount, giftMessage)
              : onContinuePressed(typedAmount, giftMessage);
          }}
          isContinueDisabled={!selectedAmount &&
            !(
              typedAmount &&
              typedAmount >= minAmount &&
              typedAmount <= maxAmount
            )}
        />
      </div>
    </Layout>
  );
};
